import {
    RequestSource,
    ShiftPreference,
    Attachment,
    CreateRequestDto,
    UpdateRequestDto,
} from '@features/requests/types';
import {
    MAX_ATTACHMENTS,
    MAX_ATTACHMENT_SIZE,
    ALLOWED_ATTACHMENT_TYPES,
    type AllowedAttachmentType,
} from '../constants';

/**
 * Validation utilities for request data
 */

/**
 * Validates request source
 */
export function validateRequestType(type: RequestSource | undefined): {
    valid: boolean;
    error?: string;
} {
    if (!type) {
        return {
            valid: false,
            error: 'Søknadstype er påkrevd. Velg Ferie, Betalt permisjon eller Skiftpreferanse.',
        };
    }

    const validSources = Object.values(RequestSource);
    if (!validSources.includes(type)) {
        return {
            valid: false,
            error: 'Ugyldig søknadstype. Velg Ferie, Betalt permisjon eller Skiftpreferanse.',
        };
    }

    return { valid: true };
}

/**
 * Validates date range
 */
export function validateDates(
    startDate: Date | undefined,
    endDate: Date | undefined,
): { valid: boolean; error?: string } {
    if (!startDate || !endDate) {
        return {
            valid: false,
            error: 'Startdato og sluttdato er påkrevd.',
        };
    }

    // Normalize to start of day for comparison
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    if (end < start) {
        return {
            valid: false,
            error: 'Sluttdato må være lik eller etter startdato.',
        };
    }

    return { valid: true };
}

/**
 * Validates attachments
 */
export function validateAttachments(attachments: Attachment[] | undefined): {
    valid: boolean;
    error?: string;
} {
    if (!attachments || attachments.length === 0) {
        return { valid: true }; // Attachments are optional
    }

    // Check maximum number of attachments
    if (attachments.length > MAX_ATTACHMENTS) {
        return {
            valid: false,
            error: `Maksimum ${MAX_ATTACHMENTS} vedlegg er tillatt.`,
        };
    }

    // Check each attachment
    for (const attachment of attachments || []) {
        // Check file type - use type assertion
        if (
            !ALLOWED_ATTACHMENT_TYPES.includes(
                attachment.type as AllowedAttachmentType,
            )
        ) {
            return {
                valid: false,
                error: `Ugyldig filtype: ${attachment.type}. Tillatte typer: PDF, JPG, PNG.`,
            };
        }

        // Check file size
        if (attachment.size > MAX_ATTACHMENT_SIZE) {
            return {
                valid: false,
                error: `Filstørrelse overskrider maksimum på 5MB: ${attachment.name}`,
            };
        }
    }

    return { valid: true };
}

/**
 * Validates shift preference
 */
export function validateShiftPreference(
    type: RequestSource | undefined,
    shiftPreference: ShiftPreference | undefined,
): { valid: boolean; error?: string } {
    // Shift preference is only required for SHIFT_PREFERENCE category (API source: WISH)
    if (type !== RequestSource.WISH) {
        return { valid: true };
    }

    if (!shiftPreference) {
        return {
            valid: false,
            error: 'Skiftpreferanse er påkrevd for Skiftpreferanse-søknader.',
        };
    }

    const validShifts = Object.values(ShiftPreference);
    if (!validShifts.includes(shiftPreference)) {
        return {
            valid: false,
            error: 'Ugyldig skiftpreferanse. Velg Morgen, Ettermiddag, Kveld, Helg eller Annet.',
        };
    }

    return { valid: true };
}

/**
 * Validates a complete create request DTO
 */
export function validateCreateRequestDto(dto: CreateRequestDto): {
    valid: boolean;
    error?: string;
} {
    // Validate type
    const typeValidation = validateRequestType(dto.type);
    if (!typeValidation.valid) {
        return typeValidation;
    }

    // Validate dates
    const dateValidation = validateDates(dto.startDate, dto.endDate);
    if (!dateValidation.valid) {
        return dateValidation;
    }

    // Validate attachments
    const attachmentValidation = validateAttachments(dto.attachments);
    if (!attachmentValidation.valid) {
        return attachmentValidation;
    }

    // Validate shift preference
    const shiftValidation = validateShiftPreference(
        dto.type,
        dto.shiftPreference,
    );
    if (!shiftValidation.valid) {
        return shiftValidation;
    }

    return { valid: true };
}

/**
 * Validates a complete update request DTO
 */
export function validateUpdateRequestDto(dto: UpdateRequestDto): {
    valid: boolean;
    error?: string;
} {
    // For update, at least one field must be provided
    if (
        !dto.startDate &&
        !dto.endDate &&
        !dto.notes &&
        !dto.attachments &&
        !dto.shiftPreference
    ) {
        return {
            valid: false,
            error: 'Minst ett felt må oppdateres.',
        };
    }

    // If dates are provided, validate them
    if (dto.startDate || dto.endDate) {
        const dateValidation = validateDates(dto.startDate, dto.endDate);
        if (!dateValidation.valid) {
            return dateValidation;
        }
    }

    // Validate attachments if provided
    if (dto.attachments !== undefined) {
        const attachmentValidation = validateAttachments(dto.attachments);
        if (!attachmentValidation.valid) {
            return attachmentValidation;
        }
    }

    return { valid: true };
}

/**
 * Formats a date for display in Norwegian format (dd/mm/yyyy)
 */
export function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

/**
 * Formats a date range for display
 */
export function formatDateRange(
    startDate: Date | null,
    endDate: Date | null,
): string {
    if (!startDate || !endDate) {
        return '';
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}
